import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { store } from './store/store';
import { AuthProvider } from './context/AuthContext';
import { ExperimentProvider } from './context/ExperimentContext';
import ErrorBoundary from './components/Common/ErrorBoundary';

function ThemeWrapper({ children }: { children: React.ReactNode }) {
  const getInitialMode = (): 'light' | 'dark' => {
    const stored = localStorage.getItem('themeMode') as 'light' | 'dark' | null;
    if (stored === 'light' || stored === 'dark') return stored;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  const [mode, setMode] = useState<'light' | 'dark'>(getInitialMode);

  useEffect(() => {
    const handler = () => setMode((m) => {
      const next = m === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
    window.addEventListener('toggle-dark-mode', handler as any);
    return () => window.removeEventListener('toggle-dark-mode', handler as any);
  }, []);

  useEffect(() => {
    document.body.dataset.theme = mode;
  }, [mode]);

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      background: {
        default: mode === 'light' ? '#f7f9fc' : '#0b1020',
        paper: mode === 'light' ? '#ffffff' : '#0f172a',
      },
      primary: {
        main: '#0b5fd7',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#0aa6a6',
        contrastText: '#ffffff',
      },
      success: { main: '#2e7d32' },
      warning: { main: '#ed6c02', contrastText: '#1a1a1a' },
      error: { main: '#c62828' },
      text: {
        primary: mode === 'light' ? '#101828' : '#e5e7eb',
        secondary: mode === 'light' ? '#475467' : '#94a3b8',
      },
      divider: mode === 'light' ? 'rgba(16,24,40,0.1)' : 'rgba(255,255,255,0.1)'
    },
    shape: { borderRadius: 10 },
    shadows: [
      'none',
      '0px 1px 2px rgba(0,0,0,0.04)',
      '0px 1px 3px rgba(16,24,40,0.1), 0px 1px 2px rgba(16,24,40,0.06)',
      '0px 2px 4px rgba(16,24,40,0.1)',
      '0px 4px 8px rgba(16,24,40,0.1)',
      '0px 8px 16px rgba(16,24,40,0.08)',
      ...Array(19).fill('0px 8px 16px rgba(16,24,40,0.08)')
    ] as any,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(90deg, #0b5fd7 0%, #0aa6a6 100%)',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.12)'
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(16,24,40,0.1), 0 1px 2px rgba(16,24,40,0.06)',
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px rgba(0,0,0,0.12)' }
          }
        }
      }
    }
  }), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Lightweight AdSense loader using window.__adsenseConfig
(function initAdLoader(){
  try {
    if ((window as any).loadAds) return; // don't override existing
    (window as any).__adsbygoogle = (window as any).__adsbygoogle || [];
    const ensureScript = (publisherId: string) => {
      const id = 'adsbygoogle-js';
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id = id;
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(publisherId)}`;
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    };
    const matchUnit = (slotId: string, slotMap: Record<string,string>): string | null => {
      if (slotMap[slotId]) return slotMap[slotId];
      // simple wildcard match for patterns ending with *
      for (const key of Object.keys(slotMap)){
        if (key.endsWith('*')){
          const prefix = key.slice(0, -1);
          if (slotId.startsWith(prefix)) return slotMap[key];
        }
      }
      return null;
    };
    const track = (event: string, data: any) => {
      try {
        const url = '/api/public/ads/track';
        const payload = JSON.stringify({ event, ...data });
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(()=>{});
        }
      } catch {}
    };
    (window as any).loadAds = function(opts: { slotId: string; tags?: string[]; pageType?: string; path?: string }){
      try {
        const cfg = (window as any).__adsenseConfig;
        if (!cfg || !cfg.enabled || !cfg.publisher_id) return;
        ensureScript(cfg.publisher_id);
        const host = document.getElementById(opts.slotId);
        if (!host) return;
        const unit = matchUnit(opts.slotId, cfg.slot_map || {});
        if (!unit) return; // no mapping → leave placeholder
        // avoid duplicate fill
        if (host.querySelector('ins.adsbygoogle')) return;
        host.innerHTML = '';
        const ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.setAttribute('style', 'display:block');
        ins.setAttribute('data-ad-client', cfg.publisher_id);
        ins.setAttribute('data-ad-slot', String(unit));
        ins.setAttribute('data-full-width-responsive', 'true');
        host.appendChild(ins);
        try { ((window as any).__adsbygoogle = (window as any).__adsbygoogle || []).push({}); } catch {}
        // Track impression/fill attempt best-effort when we render the unit
        track('impression', { slot_id: opts.slotId, unit_id: String(unit), page: opts.path || (window.location ? window.location.pathname : '') });
      } catch {}
    }

    // Auto-load AdSense config from public API to support site verification and auto ads
    try {
      fetch('/api/public/adsense/config', { credentials: 'omit' })
        .then(r => r.ok ? r.json() : null)
        .then(cfg => {
          if (!cfg) return;
          try { (window as any).__adsenseConfig = cfg; } catch {}

          // Insert Google AdSense account meta tag for site verification if publisher_id is provided
          try {
            const pid = cfg.publisher_id;
            if (pid && typeof pid === 'string') {
              let m = document.querySelector("meta[name='google-adsense-account']") as HTMLMetaElement | null;
              if (!m) {
                m = document.createElement('meta');
                m.setAttribute('name', 'google-adsense-account');
                document.head.appendChild(m);
              }
              m.setAttribute('content', pid);
            }
          } catch {}

          // If a head_snippet is provided, inject it verbatim into <head> once
          if (cfg.head_snippet && typeof cfg.head_snippet === 'string') {
            const id = 'adsense-head-snippet';
            if (!document.getElementById(id)) {
              const container = document.createElement('div');
              container.id = id;
              container.style.display = 'none';
              // Basic guard: only allow script tag snippets
              const snip = cfg.head_snippet.trim();
              if (/^<script[\s\S]*<\/script>$/i.test(snip)) {
                container.innerHTML = snip;
                document.head.appendChild(container);
              }
            }
          }
          // Ensure the core AdSense script is present when enabled
          if (cfg.enabled && cfg.publisher_id) {
            ensureScript(cfg.publisher_id);
          }
        })
        .catch(() => {});
    } catch {}
  } catch {}
})();

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <ExperimentProvider>
              <ThemeWrapper>
                <App />
              </ThemeWrapper>
            </ExperimentProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </Provider>
  </React.StrictMode>
);
